import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, Gift } from "lucide-react";
import { ReferralShareActions } from "@/components/referrals/ReferralShareActions";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    api.get(`/orders/track/${orderNumber}`).then(({ data }) => {
      setOrder(data);
      if (data?.customer?.email) {
        api.get(`/loyalty`, { params: { email: data.customer.email } })
          .then(({ data: l }) => setLoyalty(l)).catch(() => {});
        api.get("/referrals/me").then(({ data: referralData }) => {
          setReferral(referralData);
        }).catch(() => {});
      }
    });
  }, [orderNumber]);

  if (!order) {
    return (
      <div className="mx-auto max-w-[1400px] px-4 py-32 text-espresso-light md:px-10">
        Loading…
      </div>
    );
  }

  return (
    <main
      data-testid="order-confirmation"
      className="mx-auto max-w-[900px] px-4 pb-12 pt-10 md:px-10 md:py-24"
    >
      <div
        className="w-14 h-14 rounded-full border border-gold flex items-center justify-center mb-8"
      >
        <Check size={20} strokeWidth={1.5} className="text-gold" />
      </div>
      <div className="label-xs text-espresso-light">Order confirmed</div>
      <h1
        className="mt-3 font-serif text-4xl leading-[0.95] tracking-tight text-espresso md:text-7xl"
      >
        Thank you, <span className="italic">{order.customer.full_name.split(" ")[0]}.</span>
      </h1>
      <p className="mt-6 text-espresso-light max-w-md">
        We&apos;re layering your order with care. A copy of the receipt has been sent to{" "}
        <span className="text-espresso">{order.customer.email}</span>.
      </p>

      <div className="mt-10 grid gap-3 md:mt-12 md:grid-cols-2 md:gap-6">
        <div className="border border-line p-5 md:p-6">
          <div className="label-xs text-espresso-light">Order number</div>
          <div
            className="font-serif text-2xl text-espresso mt-2"
            data-testid="conf-order-number"
          >
            {order.order_number}
          </div>
        </div>
        <div className="border border-line p-5 md:p-6">
          <div className="label-xs text-espresso-light">Status</div>
          <div className="font-serif text-2xl text-espresso mt-2 capitalize">
            {order.status.replace("_", " ")}
          </div>
        </div>
        <div className="border border-line p-5 md:p-6">
          <div className="label-xs text-espresso-light">{order.fulfillment}</div>
          <div className="font-serif text-2xl text-espresso mt-2">
            {order.slot_date} · {order.slot_time}
          </div>
        </div>
        <div className="border border-line p-5 md:p-6">
          <div className="label-xs text-espresso-light">Total paid</div>
          <div className="font-serif text-2xl text-espresso mt-2">₹{order.total.toFixed(0)}</div>
        </div>
      </div>

      <div className="mt-12">
        <div className="label-xs text-espresso-light mb-4">Items</div>
        <div className="space-y-3">
          {order.items.map((it, i) => (
            <div key={i} className="flex justify-between border-b border-line pb-3">
              <span className="text-espresso">
                {it.name}{it.flavor ? ` · ${it.flavor}` : ""} × {it.quantity}
              </span>
              <span className="text-espresso">₹{it.subtotal.toFixed(0)}</span>
            </div>
          ))}
        </div>
      </div>

      {loyalty && (
        <div
          data-testid="loyalty-banner"
          className="mt-12 border border-gold/40 bg-gold/5 p-8 flex items-start gap-6"
        >
          <div
            className={
              "w-12 h-12 rounded-full border border-gold flex items-center justify-center "
              + "flex-shrink-0"
            }
          >
            <Gift size={20} strokeWidth={1.5} className="text-gold" />
          </div>
          <div className="flex-1">
            <div className="label-xs text-gold-hover">Loyalty &amp; referrals</div>
            {loyalty.pending_rewards > 0 ? (
              <div className="mt-2">
                <div className="font-serif text-2xl text-espresso">
                  A free Traditional Italian is yours next time.
                </div>
                <p className="text-espresso-light mt-2 text-sm">
                  Redeem it automatically at checkout — no code needed.
                </p>
              </div>
            ) : (
              <div className="mt-2">
                <div className="font-serif text-2xl text-espresso">
                  {loyalty.orders_until_reward} more order
                  {loyalty.orders_until_reward === 1 ? "" : "s"} until a free cup.
                </div>
                <p className="text-espresso-light mt-2 text-sm">
                  Every fifth paid order earns a Traditional Italian, on the house.
                </p>
              </div>
            )}
            {referral?.code && (
              <div className="mt-5 pt-5 border-t border-gold/30">
                <div className="text-xs text-espresso-light">
                  Share your code — friends get 10% off and every 3 paid, delivered orders earn
                  you a cup.
                </div>
                <code
                  data-testid="referral-code"
                  className="mt-3 block font-serif text-2xl text-espresso tracking-widest"
                >
                  {referral.code}
                </code>
                <ReferralShareActions code={referral.code} compact />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-10 flex flex-col gap-3 sm:mt-12 sm:flex-row sm:flex-wrap sm:gap-4">
        <Link
          to={`/track/${order.order_number}`}
          className="btn-espresso w-full sm:w-auto"
          data-testid="track-order-link"
        >
          Track order
        </Link>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.order_number}/invoice.pdf`}
          target="_blank"
          rel="noreferrer"
          className="btn-outline w-full sm:w-auto"
          data-testid="download-gst-invoice-link"
        >
          Download GST Invoice
        </a>
        <a
          href="https://g.page/r/CdeH-owIuo6vEAI/review"
          target="_blank" rel="noreferrer"
          className="btn-outline w-full sm:w-auto"
          data-testid="write-google-review-link"
        >
          Leave a Google review
        </a>
        <Link to="/menu" className="btn-outline w-full sm:w-auto">
          Order again
        </Link>
      </div>

      <div
        data-testid="order-compliance"
        className={
          "mt-14 pt-6 border-t border-line text-xs text-espresso-light flex flex-wrap "
          + "gap-x-6 gap-y-1"
        }
      >
        <span>TIRAMISÙ <span className="text-gold">&amp;</span> Co.</span>
        <span>GSTIN · <span className="text-espresso">09BQDPG9836N1Z5</span></span>
        <span>FSSAI · <span className="text-espresso">22726699000251</span></span>
      </div>
    </main>
  );
}
